import { stripe } from '../config/stripe';
import { supabase } from '../config/supabase';
export class MedicationSubscriptionService {
    /**
     * Determines subscription type based on supply days
     * 30-45 days = monthly, 60-100 days = quarterly
     */
    static determineSubscriptionType(supplyDays) {
        if (supplyDays <= 45)
            return 'monthly'; // 30-45 days = monthly
        if (supplyDays <= 100)
            return 'quarterly'; // 60-90 days = quarterly
        return 'monthly'; // Default fallback
    }
    /**
     * Calculate next prescription due date based on subscription type
     */
    static calculateNextDueDate(currentDate, supplyDays, subscriptionType) {
        const nextDue = new Date(currentDate);
        if (subscriptionType === 'monthly') {
            nextDue.setMonth(nextDue.getMonth() + 1);
        }
        else {
            nextDue.setMonth(nextDue.getMonth() + 3);
        }
        return nextDue;
    }
    /**
     * Get medication with Stripe price IDs
     */
    static async getMedication(medicationId) {
        const { data: medication, error } = await supabase
            .from('medications')
            .select('*')
            .eq('id', medicationId)
            .single();
        if (error) {
            throw new Error(`Failed to fetch medication: ${error.message}`);
        }
        return medication;
    }
    /**
     * Get patient with Stripe customer information
     */
    static async getPatient(patientId) {
        const { data: patient, error } = await supabase
            .from('patients')
            .select('*')
            .eq('id', patientId)
            .single();
        if (error) {
            throw new Error(`Failed to fetch patient: ${error.message}`);
        }
        return patient;
    }
    /**
     * Get medication dosage pricing information
     */
    static async getMedicationDosage(medicationDosageId) {
        const { data: dosage, error } = await supabase
            .from('medication_dosages')
            .select('*')
            .eq('id', medicationDosageId)
            .single();
        if (error) {
            throw new Error(`Failed to fetch medication dosage: ${error.message}`);
        }
        return dosage;
    }
    /**
     * Create initial medication subscription during first consultation
     */
    static async createMedicationSubscription(data) {
        try {
            const subscriptionType = this.determineSubscriptionType(data.supplyDays);
            const medication = await this.getMedication(data.medicationId);
            const patient = await this.getPatient(data.patientId);
            const dosage = await this.getMedicationDosage(data.medicationDosageId);
            // Ensure patient has Stripe customer ID
            if (!patient.stripe_customer_id) {
                throw new Error('Patient must have a Stripe customer ID before creating subscriptions');
            }
            // Get the appropriate Stripe price ID
            const priceId = subscriptionType === 'monthly'
                ? medication.monthly_price_stripe_id
                : medication.quarterly_price_stripe_id;
            if (!priceId) {
                throw new Error(`Missing Stripe price ID for ${subscriptionType} subscription`);
            }
            console.log(`🔄 Creating ${subscriptionType} subscription for medication ${medication.name}`);
            console.log(`💰 Price ID: ${priceId}, Unit Price: $${dosage.unit_price}`);
            // Create Stripe subscription
            const subscription = await stripe.subscriptions.create({
                customer: patient.stripe_customer_id,
                items: [{ price: priceId }],
                metadata: {
                    medication_id: data.medicationId,
                    medication_dosage_id: data.medicationDosageId,
                    patient_id: data.patientId,
                    subscription_type: subscriptionType,
                    supply_days: data.supplyDays.toString()
                },
                payment_behavior: 'default_incomplete',
                payment_settings: {
                    save_default_payment_method: 'on_subscription'
                },
                expand: ['latest_invoice.payment_intent']
            });
            // Update patient_medication_preferences with subscription details
            const { data: preference, error: preferenceError } = await supabase
                .from('patient_medication_preferences')
                .update({
                subscription_type: subscriptionType,
                stripe_subscription_id: subscription.id,
                next_prescription_due: this.calculateNextDueDate(new Date(), data.supplyDays, subscriptionType).toISOString(),
                supply_days: data.supplyDays,
                auto_refill: true
            })
                .eq('patient_id', data.patientId)
                .eq('medication_id', data.medicationId)
                .eq('medication_dosage_id', data.medicationDosageId)
                .select()
                .single();
            if (preferenceError) {
                // Cleanup: cancel the Stripe subscription if database update fails
                await stripe.subscriptions.cancel(subscription.id);
                throw new Error(`Failed to update medication preference: ${preferenceError.message}`);
            }
            console.log('✅ Medication subscription created successfully');
            return {
                success: true,
                subscription,
                preference,
                client_secret: subscription.latest_invoice?.payment_intent?.client_secret
            };
        }
        catch (error) {
            console.error('❌ Failed to create medication subscription:', error);
            return {
                success: false,
                error: error instanceof Error ? error.message : 'Unknown error occurred'
            };
        }
    }
    /**
     * Handle subscription changes when supply days are modified
     */
    static async changeSubscriptionType(data) {
        try {
            // Get current preference
            const { data: preference, error: preferenceError } = await supabase
                .from('patient_medication_preferences')
                .select('*')
                .eq('id', data.preferenceId)
                .single();
            if (preferenceError || !preference) {
                throw new Error(`Failed to fetch medication preference: ${preferenceError?.message}`);
            }
            const currentType = preference.subscription_type;
            const newType = this.determineSubscriptionType(data.newSupplyDays);
            console.log(`🔄 Subscription change: ${currentType} → ${newType} (${preference.supply_days} → ${data.newSupplyDays} days)`);
            // If subscription type hasn't changed, just update supply days metadata
            if (currentType === newType) {
                await stripe.subscriptions.update(preference.stripe_subscription_id, {
                    metadata: {
                        supply_days: data.newSupplyDays.toString(),
                        ...(data.newMedicationDosageId && { medication_dosage_id: data.newMedicationDosageId })
                    }
                });
                // Update database
                const updateData = {
                    supply_days: data.newSupplyDays
                };
                if (data.newMedicationDosageId) {
                    updateData.medication_dosage_id = data.newMedicationDosageId;
                }
                const { error: updateError } = await supabase
                    .from('patient_medication_preferences')
                    .update(updateData)
                    .eq('id', data.preferenceId);
                if (updateError) {
                    throw new Error(`Failed to update preference: ${updateError.message}`);
                }
                console.log('✅ Supply days updated without subscription type change');
                return { success: true, action: 'supply_days_updated' };
            }
            // Different subscription type - need to transition subscription
            return await this.transitionSubscription(preference, newType, data.newSupplyDays, data.newMedicationDosageId);
        }
        catch (error) {
            console.error('❌ Failed to change subscription type:', error);
            return {
                success: false,
                error: error instanceof Error ? error.message : 'Unknown error occurred'
            };
        }
    }
    /**
     * Transition subscription from one type to another using Stripe Subscription Schedules
     */
    static async transitionSubscription(preference, newType, newSupplyDays, newMedicationDosageId) {
        try {
            const medication = await this.getMedication(preference.medication_id);
            // Get the new price ID
            const newPriceId = newType === 'monthly'
                ? medication.monthly_price_stripe_id
                : medication.quarterly_price_stripe_id;
            if (!newPriceId) {
                throw new Error(`Missing Stripe price ID for ${newType} subscription`);
            }
            // Get current subscription details
            const currentSub = await stripe.subscriptions.retrieve(preference.stripe_subscription_id);
            const prorationDate = Math.floor(Date.now() / 1000);
            console.log(`🔄 Transitioning subscription from ${preference.subscription_type} to ${newType}`);
            // Create subscription schedule for seamless transition
            const schedule = await stripe.subscriptionSchedules.create({
                from_subscription: preference.stripe_subscription_id,
                phases: [
                    {
                        // Current phase ends immediately with proration
                        start_date: currentSub.current_period_start,
                        end_date: prorationDate,
                        items: currentSub.items.data.map(item => ({
                            price: item.price.id,
                            quantity: item.quantity
                        })),
                        proration_behavior: 'always_invoice'
                    },
                    {
                        // New phase starts immediately
                        start_date: prorationDate,
                        items: [{
                                price: newPriceId,
                                quantity: 1
                            }],
                        iterations: newType === 'monthly' ? 12 : 4, // Limit iterations for easier management
                        metadata: {
                            medication_id: preference.medication_id,
                            medication_dosage_id: newMedicationDosageId || preference.medication_dosage_id,
                            patient_id: preference.patient_id,
                            subscription_type: newType,
                            supply_days: newSupplyDays.toString()
                        }
                    }
                ]
            });
            // Update database with new subscription details
            const updateData = {
                subscription_type: newType,
                supply_days: newSupplyDays,
                next_prescription_due: this.calculateNextDueDate(new Date(), newSupplyDays, newType).toISOString(),
                stripe_subscription_id: schedule.subscription // Update to the new subscription ID
            };
            if (newMedicationDosageId) {
                updateData.medication_dosage_id = newMedicationDosageId;
            }
            const { error: updateError } = await supabase
                .from('patient_medication_preferences')
                .update(updateData)
                .eq('id', preference.id);
            if (updateError) {
                // Cleanup: cancel the subscription schedule if database update fails
                await stripe.subscriptionSchedules.cancel(schedule.id);
                throw new Error(`Failed to update preference after transition: ${updateError.message}`);
            }
            console.log('✅ Subscription transitioned successfully');
            return {
                success: true,
                action: 'subscription_transitioned',
                schedule,
                old_subscription_id: preference.stripe_subscription_id,
                new_subscription_id: schedule.subscription
            };
        }
        catch (error) {
            console.error('❌ Failed to transition subscription:', error);
            return {
                success: false,
                error: error instanceof Error ? error.message : 'Unknown error occurred'
            };
        }
    }
    /**
     * Cancel a medication subscription
     */
    static async cancelSubscription(preferenceId, reason) {
        try {
            const { data: preference, error: preferenceError } = await supabase
                .from('patient_medication_preferences')
                .select('*')
                .eq('id', preferenceId)
                .single();
            if (preferenceError || !preference) {
                throw new Error(`Failed to fetch medication preference: ${preferenceError?.message}`);
            }
            if (!preference.stripe_subscription_id) {
                throw new Error('No active subscription found');
            }
            // Cancel the Stripe subscription
            await stripe.subscriptions.cancel(preference.stripe_subscription_id, {
                cancellation_details: {
                    comment: reason || 'Cancelled by patient'
                }
            });
            // Update database
            const { error: updateError } = await supabase
                .from('patient_medication_preferences')
                .update({
                stripe_subscription_id: null,
                auto_refill: false
            })
                .eq('id', preferenceId);
            if (updateError) {
                throw new Error(`Failed to update preference after cancellation: ${updateError.message}`);
            }
            console.log('✅ Subscription cancelled successfully');
            return { success: true, action: 'subscription_cancelled' };
        }
        catch (error) {
            console.error('❌ Failed to cancel subscription:', error);
            return {
                success: false,
                error: error instanceof Error ? error.message : 'Unknown error occurred'
            };
        }
    }
}
